import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listCourses, courseVideoUrl, fetchCourseVtt, fetchCourseOutline, courseResourcesUrl,
} from '@/api/client';
import type {
  CourseChapterRef, CourseList, CourseOutlineChapter, CourseSummary, CourseTrack,
} from '@/api/client';
import { formatTime, parseVtt } from '@/lib/vtt';
import type { VttCue } from '@/lib/vtt';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight,
  Download, GraduationCap, Info, Loader2, RotateCcw, Search, X,
} from 'lucide-react';

import { apiErrorText } from '../../lib/describeApiError';
import SlideCard from './SlideCard';
import {
  fractionSeen, markWatched, readProgress, recordPosition, resumeAt, writeProgress,
} from './courseProgress';
import type { ProgressMap } from './courseProgress';
/**
 * /courses — the owner's videos, in two tracks: the beginner course
 * (F00–F17, for technical people new to patent drafting) and the
 * Draft Studio workflow lessons (D01–D08). The server reads the videos,
 * their subtitles and the beginner course's catalogue from the folder
 * COURSE_VIDEOS_DIR points at; the catalogue is listed in full even
 * before the files are there, so drafters can see what is coming. Open
 * to every signed-in user.
 */

interface Props {
  onBack?: () => void;
  /** The signed-in user, so watched lessons and positions are theirs. */
  userId?: string | number | null;
}

const LAST_LESSON_KEY = 'courses_last_lesson';

// After the drafter scrolls the transcript by hand, leave it alone for
// this long so reading ahead is not undone by the next spoken line.
const MANUAL_SCROLL_GRACE_MS = 4000;
// How often the position is saved while a lesson plays.
const SAVE_EVERY_S = 5;

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors';

const smallBtn =
  'inline-flex h-8 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2.5 ' +
  'text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

const sectionHeading = 'mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500';

function readLastLesson(): string | null {
  try {
    return localStorage.getItem(LAST_LESSON_KEY);
  } catch {
    return null;
  }
}

function rememberLesson(id: string) {
  try {
    localStorage.setItem(LAST_LESSON_KEY, id);
  } catch {
    // Private mode or storage disabled — the page just forgets.
  }
}

/** The remembered lesson while it can still be played, otherwise the
 * first one that can. */
function initialLesson(courses: CourseSummary[]): string | null {
  const playable = courses.filter((c) => c.available);
  const last = readLastLesson();
  return (playable.find((c) => c.id === last) ?? playable[0])?.id ?? null;
}

/** "92 min" / "1 h 32 min". */
function formatTotal(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** The text with every case-insensitive occurrence of ``query`` marked. */
function Highlighted({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let from = 0;
  let at = lower.indexOf(needle);
  while (at >= 0) {
    if (at > from) parts.push(text.slice(from, at));
    parts.push(
      <mark key={at} className="rounded-sm bg-amber-200/70 px-0.5 text-zinc-900">
        {text.slice(at, at + needle.length)}
      </mark>,
    );
    from = at + needle.length;
    at = lower.indexOf(needle, from);
  }
  if (from < text.length) parts.push(text.slice(from));
  return <>{parts}</>;
}

interface PlayerProps {
  course: CourseSummary;
  byId: Record<string, CourseSummary>;
  previous: CourseSummary | null;
  next: CourseSummary | null;
  onSelect: (id: string) => void;
  progress: ProgressMap;
  onProgress: (update: (map: ProgressMap) => ProgressMap) => void;
}

/** Player, details, chapters, transcript and lesson notes for one
 * lesson. Keyed by the lesson id, so switching lessons starts it fresh. */
function LessonPlayer({
  course, byId, previous, next, onSelect, progress, onProgress,
}: PlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLTrackElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const manualScrollAt = useRef(0);
  const lastSavedAt = useRef(0);
  const [subtitleUrl, setSubtitleUrl] = useState<string | null>(null);
  const [cues, setCues] = useState<VttCue[]>([]);
  const [fileChapters, setFileChapters] = useState<VttCue[]>([]);
  const [outline, setOutline] = useState<CourseOutlineChapter[]>([]);
  const [activeCue, setActiveCue] = useState(-1);
  const [activeChapter, setActiveChapter] = useState(-1);
  const [videoFailed, setVideoFailed] = useState(false);
  const [resumedAt, setResumedAt] = useState<number | null>(null);
  const [ended, setEnded] = useState(false);
  const [query, setQuery] = useState('');
  const [panel, setPanel] = useState<'transcript' | 'notes'>('transcript');

  useEffect(() => {
    if (!course.has_subtitles) return;
    let cancelled = false;
    let blobUrl: string | null = null;
    fetchCourseVtt(course.id, 'subtitles')
      .then((text) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        setSubtitleUrl(blobUrl);
        setCues(parseVtt(text));
      })
      .catch(() => {
        // Subtitles are an extra; the lesson plays without them.
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [course.id, course.has_subtitles]);

  useEffect(() => {
    if (!course.has_chapters) return;
    let cancelled = false;
    fetchCourseVtt(course.id, 'chapters')
      .then((text) => {
        if (!cancelled) setFileChapters(parseVtt(text));
      })
      .catch(() => {
        // No chapter list then; the video is unaffected.
      });
    return () => { cancelled = true; };
  }, [course.id, course.has_chapters]);

  useEffect(() => {
    if (!course.has_outline) return;
    let cancelled = false;
    fetchCourseOutline(course.id)
      .then((data) => {
        if (!cancelled) setOutline(Array.isArray(data?.chapters) ? data.chapters : []);
      })
      .catch(() => {
        // Notes are an extra; the lesson plays without them.
      });
    return () => { cancelled = true; };
  }, [course.id, course.has_outline]);

  // The catalogue's chapters when it has them, else the chapters file.
  const chapters: CourseChapterRef[] = useMemo(() => {
    if (course.chapters && course.chapters.length > 0) return course.chapters;
    return fileChapters.map((c) => ({ title: c.text, start: c.start }));
  }, [course.chapters, fileChapters]);

  // The subtitles, or failing those the narration, one line per chapter.
  const transcript: VttCue[] = useMemo(() => {
    if (cues.length > 0) return cues;
    return outline
      .filter((c) => c.narration)
      .map((c, i, all) => ({
        start: c.start,
        end: all[i + 1]?.start ?? Number.POSITIVE_INFINITY,
        text: c.narration,
      }));
  }, [cues, outline]);

  const hasNotes = outline.some((c) => c.slides && c.slides.length > 0);

  // Captions start off; the player's captions button turns them on.
  // 'hidden' rather than 'disabled' keeps the cues loaded, so they show
  // at once when switched on.
  useEffect(() => {
    const track = trackRef.current?.track;
    if (subtitleUrl && track) track.mode = 'hidden';
  }, [subtitleUrl]);

  const save = (force: boolean) => {
    const v = videoRef.current;
    if (!v || !Number.isFinite(v.duration) || v.duration <= 0) return;
    if (!force && Math.abs(v.currentTime - lastSavedAt.current) < SAVE_EVERY_S) return;
    lastSavedAt.current = v.currentTime;
    onProgress((map) => recordPosition(map, course.id, v.currentTime, v.duration));
  };

  // timeupdate fires several times a second. Setting the same index
  // again is a no-op for React, so the panels re-render only when the
  // spoken line or the chapter changes.
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime;
    if (transcript.length > 0) {
      setActiveCue(transcript.findIndex((c) => t >= c.start && t < c.end));
    }
    if (chapters.length > 0) {
      let current = -1;
      chapters.forEach((c, i) => { if (t >= c.start) current = i; });
      setActiveChapter(current);
    }
    save(false);
  };

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    const at = resumeAt(progress[course.id], v.duration);
    if (at !== null) {
      v.currentTime = at;
      lastSavedAt.current = at;
      setResumedAt(at);
    }
  };

  const onEnded = () => {
    const v = videoRef.current;
    setEnded(true);
    onProgress((map) => markWatched(map, course.id, v?.duration || 0));
  };

  const keepActiveCueVisible = useCallback(() => {
    const video = videoRef.current;
    const box = transcriptRef.current;
    // Only while the video plays and nothing is being searched: a
    // paused or searching drafter is reading, not following along.
    if (activeCue < 0 || !video || video.paused || !box || query) return;
    if (Date.now() - manualScrollAt.current < MANUAL_SCROLL_GRACE_MS) return;
    const row = box.querySelector<HTMLElement>(`[data-cue="${activeCue}"]`);
    if (!row) return;
    // The box is `relative`, so a row's offsetTop is measured from its
    // top. Moving scrollTop scrolls the box alone; scrollIntoView would
    // drag the page along with it.
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < box.scrollTop || bottom > box.scrollTop + box.clientHeight) {
      box.scrollTop = Math.max(0, top - box.clientHeight / 3);
    }
  }, [activeCue, query]);

  useEffect(() => { keepActiveCueVisible(); }, [keepActiveCueVisible]);

  const noteManualScroll = () => { manualScrollAt.current = Date.now(); };

  const seek = (seconds: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = seconds;
    setEnded(false);
  };

  const startOver = () => {
    seek(0);
    setResumedAt(null);
  };

  const needle = query.trim();
  const shown = needle
    ? transcript
      .map((c, i) => ({ c, i }))
      .filter(({ c }) => c.text.toLowerCase().includes(needle.toLowerCase()))
    : transcript.map((c, i) => ({ c, i }));

  const prerequisites = (course.prerequisites || []).filter((id) => id !== course.id);
  const watched = !!progress[course.id]?.watched;

  return (
    <div>
      <video
        key={course.id}
        ref={videoRef}
        controls
        // No download button in the player menu, and no "Save video
        // as" on right-click. A determined viewer can still fetch the
        // stream; this keeps the lessons from being saved by accident.
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        preload="metadata"
        src={courseVideoUrl(course.id)}
        playsInline
        className="w-full bg-black rounded-sm"
        data-testid="course-video"
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onPlay={() => { setEnded(false); keepActiveCueVisible(); }}
        onPause={() => save(true)}
        onEnded={onEnded}
        onError={() => setVideoFailed(true)}
      >
        {subtitleUrl && (
          <track
            ref={trackRef}
            kind="subtitles"
            srcLang="en"
            label="English"
            src={subtitleUrl}
          />
        )}
      </video>
      {videoFailed && (
        <div
          className="mt-2 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-start gap-2"
          data-testid="course-video-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          This video could not be played. It may be missing on the server or
          in a format this browser does not support.
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-600">
        {resumedAt !== null && (
          <span className="inline-flex items-center gap-2" data-testid="course-resumed">
            Picked up where you left off, at {formatTime(resumedAt)}.
            <button type="button" className={smallBtn} onClick={startOver}>
              <RotateCcw size={12} /> Start from the beginning
            </button>
          </span>
        )}
        <span className="ml-auto inline-flex gap-2">
          <button
            type="button"
            className={smallBtn}
            disabled={!previous}
            onClick={() => previous && onSelect(previous.id)}
            data-testid="course-previous"
            title={previous ? `${previous.id} ${previous.title}` : 'This is the first lesson'}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <button
            type="button"
            className={smallBtn}
            disabled={!next}
            onClick={() => next && onSelect(next.id)}
            data-testid="course-next"
            title={next ? `${next.id} ${next.title}` : 'This is the last lesson'}
          >
            Next <ChevronRight size={14} />
          </button>
        </span>
      </div>

      {ended && next && (
        <div
          className="mt-3 flex flex-wrap items-center gap-3 rounded-sm border border-[#002FA7]/30 bg-[#002FA7]/5 px-3 py-2 text-sm"
          data-testid="course-up-next"
        >
          <CheckCircle2 size={16} className="text-[#002FA7]" />
          <span className="text-zinc-700">Lesson watched. Up next:</span>
          <button
            type="button"
            className="font-medium text-[#002FA7] hover:underline"
            onClick={() => onSelect(next.id)}
          >
            {next.id} {next.title}
          </button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <div>
            <h2
              className="text-lg font-semibold tracking-tight text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              <span className="font-mono text-sm text-zinc-500 mr-2">{course.id}</span>{' '}
              {course.title}
            </h2>
            <div className="mt-0.5 flex items-center gap-3 text-xs text-zinc-500">
              {course.duration && <span className="tabular-nums">{course.duration}</span>}
              {watched && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 size={12} /> Watched
                </span>
              )}
            </div>
            <dl className="mt-3 space-y-3 text-sm">
              {course.summary && (
                <div>
                  <dt className="text-xs font-medium text-zinc-500">What it covers</dt>
                  <dd className="text-zinc-700">{course.summary}</dd>
                </div>
              )}
              {course.audience && (
                <div>
                  <dt className="text-xs font-medium text-zinc-500">For</dt>
                  <dd className="text-zinc-700">{course.audience}</dd>
                </div>
              )}
              {prerequisites.length > 0 && (
                <div data-testid="course-prerequisites">
                  <dt className="text-xs font-medium text-zinc-500">Watch first</dt>
                  <dd className="flex flex-wrap gap-x-3 gap-y-1">
                    {prerequisites.map((id) => {
                      const lesson = byId[id];
                      const label = lesson ? `${id} ${lesson.title}` : id;
                      const seen = !!progress[id]?.watched;
                      return lesson?.available ? (
                        <button
                          key={id}
                          type="button"
                          onClick={() => onSelect(id)}
                          className="inline-flex items-center gap-1 text-left text-[#002FA7] hover:underline"
                        >
                          {seen && <CheckCircle2 size={12} className="text-emerald-700" aria-label="watched" />}
                          {label}
                        </button>
                      ) : (
                        <span key={id} className="text-zinc-500">{label}</span>
                      );
                    })}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {chapters.length > 0 && (
            <section aria-label="Chapters" data-testid="course-chapters">
              <h3 className={sectionHeading}>Chapters</h3>
              <ol className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
                {chapters.map((c, i) => {
                  const active = i === activeChapter;
                  return (
                    <li key={`${c.start}-${i}`}>
                      <button
                        type="button"
                        onClick={() => seek(c.start)}
                        aria-current={active ? 'true' : undefined}
                        className={
                          'w-full flex items-baseline gap-3 border-l-2 px-3 py-2 text-left text-sm transition-colors ' +
                          (active
                            ? 'border-[#002FA7] bg-[#002FA7]/5 text-zinc-900'
                            : 'border-transparent text-zinc-700 hover:bg-zinc-50')
                        }
                      >
                        <span className="font-mono text-xs tabular-nums text-zinc-500 shrink-0">
                          {formatTime(c.start)}
                        </span>
                        <span className="flex-1">{c.title}</span>
                        {c.kind && (
                          <span className="shrink-0 text-[10px] uppercase tracking-wide text-zinc-400">
                            {c.kind === 'live demo' ? 'On screen' : c.kind === 'slides' ? 'Slides' : c.kind}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </div>

        {(transcript.length > 0 || hasNotes) && (
          <div>
            <div className="mb-2 flex items-center gap-1" role="tablist" aria-label="Lesson text">
              {transcript.length > 0 && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={panel === 'transcript'}
                  onClick={() => setPanel('transcript')}
                  className={
                    'rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-wide ' +
                    (panel === 'transcript' ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500 hover:text-zinc-800')
                  }
                  data-testid="course-tab-transcript"
                >
                  Transcript
                </button>
              )}
              {hasNotes && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={panel === 'notes' || transcript.length === 0}
                  onClick={() => setPanel('notes')}
                  className={
                    'rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-wide ' +
                    (panel === 'notes' || transcript.length === 0
                      ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500 hover:text-zinc-800')
                  }
                  data-testid="course-tab-notes"
                >
                  Lesson notes
                </button>
              )}
            </div>

            {panel === 'transcript' && transcript.length > 0 && (
              <section aria-label="Transcript" data-testid="course-transcript">
                <label className="mb-2 flex items-center gap-2 rounded-sm border border-zinc-200 px-2 py-1.5 text-sm focus-within:border-[#002FA7]">
                  <Search size={14} className="shrink-0 text-zinc-400" aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the transcript"
                    aria-label="Search the transcript"
                    className="w-full bg-transparent outline-none placeholder:text-zinc-400"
                    data-testid="course-transcript-search"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      aria-label="Clear the search"
                      className="text-zinc-400 hover:text-zinc-700"
                    >
                      <X size={14} />
                    </button>
                  )}
                </label>
                {needle && (
                  <p className="mb-1 text-xs text-zinc-500" data-testid="course-transcript-count">
                    {shown.length === 0
                      ? 'No line mentions that.'
                      : `${shown.length === 1 ? '1 line mentions' : `${shown.length} lines mention`} “${needle}”. Click one to jump there.`}
                  </p>
                )}
                <div
                  ref={transcriptRef}
                  onWheel={noteManualScroll}
                  onTouchMove={noteManualScroll}
                  onKeyDown={noteManualScroll}
                  className="relative max-h-96 overflow-y-auto border border-zinc-200 rounded-sm"
                >
                  <ol>
                    {shown.map(({ c, i }) => {
                      const active = i === activeCue;
                      return (
                        <li key={`${c.start}-${i}`}>
                          <button
                            type="button"
                            data-cue={i}
                            aria-current={active ? 'true' : undefined}
                            onClick={() => seek(c.start)}
                            className={
                              'w-full flex items-baseline gap-3 border-l-2 px-3 py-1.5 text-left text-sm transition-colors ' +
                              (active
                                ? 'border-[#002FA7] bg-[#002FA7]/5 text-zinc-900'
                                : 'border-transparent text-zinc-600 hover:bg-zinc-50')
                            }
                          >
                            <span className="font-mono text-xs tabular-nums text-zinc-500 shrink-0">
                              {formatTime(c.start)}
                            </span>
                            <span><Highlighted text={c.text} query={needle} /></span>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </section>
            )}

            {(panel === 'notes' || transcript.length === 0) && hasNotes && (
              <div className="space-y-4" data-testid="course-notes">
                <p className="text-xs text-zinc-500">
                  What is shown on screen in each chapter. Click a chapter
                  time to jump there.
                </p>
                {outline.filter((c) => c.slides && c.slides.length > 0).map((c) => (
                  <div key={`${c.start}-${c.title}`}>
                    <button
                      type="button"
                      onClick={() => seek(c.start)}
                      className="mb-1.5 flex items-baseline gap-2 text-left text-sm font-medium text-zinc-900 hover:text-[#002FA7]"
                    >
                      <span className="font-mono text-xs tabular-nums text-zinc-500">{formatTime(c.start)}</span>
                      {c.title}
                    </button>
                    <div className="space-y-2">
                      {c.slides.map((slide, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <SlideCard key={i} slide={slide} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CoursesPage({ onBack, userId }: Props) {
  const [catalogue, setCatalogue] = useState<CourseList | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [trackId, setTrackId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressMap>(() => readProgress(userId));

  useEffect(() => { setProgress(readProgress(userId)); }, [userId]);

  const updateProgress = useCallback((update: (map: ProgressMap) => ProgressMap) => {
    setProgress((map) => {
      const next = update(map);
      if (next !== map) writeProgress(userId, next);
      return next;
    });
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listCourses();
        if (cancelled) return;
        setCatalogue(data);
        const first = initialLesson(data.courses || []);
        setSelectedId(first);
        const tracks = data.tracks || [];
        const ofFirst = (data.courses || []).find((c) => c.id === first)?.track;
        setTrackId(
          tracks.find((t) => t.id === ofFirst)?.id
          ?? tracks.find((t) => (data.courses || []).some((c) => c.track === t.id))?.id
          ?? null,
        );
      } catch (e: any) {
        if (!cancelled) setErr(apiErrorText(e, 'Could not load the courses.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const courses = useMemo(() => catalogue?.courses || [], [catalogue]);
  const byId = useMemo(
    () => Object.fromEntries(courses.map((c) => [c.id, c])) as Record<string, CourseSummary>,
    [courses],
  );
  // Tracks that have lessons; none from an older server means one list.
  const tracks: CourseTrack[] = (catalogue?.tracks || [])
    .filter((t) => courses.some((c) => c.track === t.id));
  const track = tracks.find((t) => t.id === trackId) || null;
  const listed = track ? courses.filter((c) => c.track === track.id) : courses;
  const selected = courses.find((c) => c.id === selectedId && c.available) || null;
  const nothingToPlay = !!catalogue
    && (!catalogue.videos_dir_found || !courses.some((c) => c.available));

  // Previous / next among the lesson's own track, skipping lessons not on
  // the server yet.
  const playableInTrack = selected
    ? courses.filter((c) => c.available && (!selected.track || c.track === selected.track))
    : [];
  const at = selected ? playableInTrack.findIndex((c) => c.id === selected.id) : -1;
  const previous = at > 0 ? playableInTrack[at - 1] : null;
  const next = at >= 0 && at < playableInTrack.length - 1 ? playableInTrack[at + 1] : null;

  const choose = (id: string) => {
    setSelectedId(id);
    rememberLesson(id);
    const lessonTrack = byId[id]?.track;
    if (lessonTrack && tracks.some((t) => t.id === lessonTrack)) setTrackId(lessonTrack);
  };

  const chooseTrack = (id: string) => {
    setTrackId(id);
    // Show a lesson of that track: the remembered one if it is there.
    if (selected?.track === id) return;
    const inTrack = courses.filter((c) => c.track === id && c.available);
    const resume = inTrack.find((c) => !progress[c.id]?.watched) ?? inTrack[0];
    if (resume) {
      setSelectedId(resume.id);
      rememberLesson(resume.id);
    }
  };

  const watchedIn = (ids: string[]) => ids.filter((id) => progress[id]?.watched).length;

  return (
    <div className="min-h-screen bg-white" data-testid="courses-page">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap size={20} className="text-[#002FA7]" />
            <h1
              className="text-lg font-semibold tracking-tight text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Courses
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {catalogue?.resources && (
              catalogue.resources.available ? (
                <a
                  href={courseResourcesUrl()}
                  download
                  className={outlineBtn}
                  data-testid="courses-resources-link"
                  title="Download the documents the lessons use (sample disclosure, tea letter and more) as one ZIP"
                >
                  <Download size={14} /> Resources
                </a>
              ) : (
                <span
                  className={`${outlineBtn} cursor-not-allowed opacity-50`}
                  aria-disabled="true"
                  data-testid="courses-resources-link"
                  title="No course documents have been added to this server yet"
                >
                  <Download size={14} /> Resources
                </span>
              )
            )}
            {onBack && (
              <button
                type="button"
                className={outlineBtn}
                onClick={onBack}
                data-testid="courses-back-button"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-6 pb-16">
        {tracks.length > 1 && (
          <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Courses">
            {tracks.map((t) => {
              const active = t.id === track?.id;
              const seen = watchedIn(t.lesson_ids);
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => chooseTrack(t.id)}
                  data-testid={`course-track-${t.id}`}
                  className={
                    'rounded-sm border px-3 py-2 text-left transition-colors ' +
                    (active
                      ? 'border-[#002FA7] bg-[#002FA7]/5'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50')
                  }
                >
                  <span className="block text-sm font-medium text-zinc-900">{t.title}</span>
                  <span className="block text-xs text-zinc-500 tabular-nums">
                    {t.lesson_ids.length} lessons
                    {t.total_seconds > 0 && ` · ${formatTotal(t.total_seconds)}`}
                    {seen > 0 && ` · ${seen} watched`}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p className="mb-6 text-sm text-zinc-500" data-testid="courses-intro">
          {track?.intro ?? 'Short videos on using Draft Studio. Suggested order: D01 to D07, then D08.'}
        </p>

        {err && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
            <AlertTriangle size={16} />
            {err}
          </div>
        )}

        {nothingToPlay && (
          <div
            className="mb-6 p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm text-zinc-700 flex items-start gap-2"
            data-testid="courses-empty-note"
          >
            <Info size={16} className="mt-0.5 shrink-0 text-[#002FA7]" />
            <span>
              The videos have not been added to this server yet. An
              administrator sets <code className="font-mono text-xs">COURSE_VIDEOS_DIR</code> to
              the folder that holds them (see the README).
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-10 justify-center">
            <Loader2 size={14} className="animate-spin" />
            Loading courses…
          </div>
        ) : catalogue && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <ol aria-label="Lessons" className="space-y-1">
              {listed.map((c) => {
                const isSelected = selected?.id === c.id;
                const seen = fractionSeen(progress[c.id], c.seconds);
                const watched = !!progress[c.id]?.watched;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-testid={`course-item-${c.id}`}
                      disabled={!c.available}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => choose(c.id)}
                      className={
                        'w-full rounded-sm border px-3 py-2 text-left transition-colors ' +
                        (isSelected
                          ? 'border-[#002FA7] bg-[#002FA7]/5'
                          : c.available
                            ? 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                            : 'border-zinc-200 bg-zinc-50 cursor-not-allowed')
                      }
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-xs text-zinc-500 shrink-0">{c.id}</span>{' '}
                        <span
                          className={
                            'flex-1 text-sm font-medium ' +
                            (c.available ? 'text-zinc-900' : 'text-zinc-500')
                          }
                        >
                          {c.title}
                        </span>
                        {watched && (
                          <CheckCircle2
                            size={14}
                            className="shrink-0 self-center text-emerald-600"
                            aria-label="Watched"
                            data-testid={`course-watched-${c.id}`}
                          />
                        )}
                      </span>
                      {(c.duration || !c.available) && (
                        <span className="mt-0.5 flex gap-2 text-xs text-zinc-500">
                          {c.duration && <span className="tabular-nums">{c.duration}</span>}
                          {!c.available && <span>Not added yet</span>}
                        </span>
                      )}
                      {seen > 0 && !watched && (
                        <span className="mt-1 block h-0.5 w-full rounded-full bg-zinc-200" aria-hidden>
                          <span
                            className="block h-0.5 rounded-full bg-[#002FA7]"
                            style={{ width: `${Math.round(seen * 100)}%` }}
                          />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>

            {/* Player first on narrow screens, so picking a lesson from the
                list below shows it without scrolling past every entry. */}
            <div className="min-w-0 order-first lg:order-none">
              {selected ? (
                <LessonPlayer
                  key={selected.id}
                  course={selected}
                  byId={byId}
                  previous={previous}
                  next={next}
                  onSelect={choose}
                  progress={progress}
                  onProgress={updateProgress}
                />
              ) : (
                <div className="flex aspect-video items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-zinc-50 text-sm text-zinc-500">
                  No lesson to play yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
